import React, { useState, Component, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  BookOpen, 
  Users, 
  BarChart3,
  Menu,
  X,
  Shield,
  Crown,
  GraduationCap,
  Briefcase,
  FolderOpen,
  Users2,
  Database,
  AlertTriangle,
  RefreshCw,
  XCircle,
  Video,
  Trash2,
  ChevronDown,
  LogOut,
  User,
  Settings,
  Bell,
  Search,
  MessageCircle,
  MessageSquare,
  Activity,
  Target,
  UserPlus,
  Lock,
  Brain,
  HelpCircle,
  Building2,
  CreditCard,
  Banknote,
  UserCheck,
  FileSearch
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen admin-content flex items-center justify-center p-4">
          <div className="admin-card p-6 sm:p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="admin-section-title mb-2">Founder Panel Error</h2>
              <p className="admin-section-subtitle mb-4">
                There was an error loading the founder panel. Please try refreshing the page.
              </p>
              <button 
                onClick={() => window.location.reload()} 
                className="admin-btn-primary flex items-center mx-auto"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const FounderLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useRealtime();

  // Enhanced device detection
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Close sidebar when route changes on mobile and tablet
  useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, isTablet]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Founder navigation structure
  const navigation = [
    {
      name: 'Dashboard',
      href: '/founder',
      icon: Home,
      section: 'main'
    },
    {
      name: 'Admin Management',
      href: '/founder/admin-management',
      icon: UserCheck,
      section: 'management'
    },
    {
      name: 'Admin Logs',
      href: '/founder/admin-logs',
      icon: FileSearch,
      section: 'management'
    },
    {
      name: 'Admin Panel',
      href: '/admin',
      icon: Shield,
      section: 'management'
    },
    {
      name: 'User Management',
      href: '/admin/user-management',
      icon: Users,
      section: 'management'
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      section: 'analytics'
    },
    {
      name: 'System Settings',
      href: '/admin/system-settings',
      icon: Settings,
      section: 'settings'
    }
  ];

  const groupedNavigation = navigation.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {});

  const sectionNames = {
    main: 'Main',
    management: 'Management',
    analytics: 'Analytics',
    settings: 'Settings'
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      await signOut(auth).catch(() => {});
      navigate('/');
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen admin-content flex">
        {/* Mobile and tablet sidebar overlay */}
        {sidebarOpen && (isMobile || isTablet) && (
          <div 
            className="fixed inset-0 z-40 bg-black bg-opacity-50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-64 sm:w-72 admin-sidebar shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
            sidebarOpen ? 'translate-x-0' : ((isMobile || isTablet) ? '-translate-x-full' : 'lg:translate-x-0')
          }`}
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 admin-sidebar-header">
            <Link to="/founder" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Crown size={16} className="text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-white">Founder</span>
            </Link>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSidebarOpen(false);
              }}
              className="lg:hidden p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors touch-manipulation"
              aria-label="Close sidebar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="mt-6 px-3 flex-1 overflow-y-auto">
            <div className="space-y-4">
              {Object.entries(groupedNavigation).map(([section, items]) => (
                <div key={section}>
                  {/* Section Header */}
                  <div className="flex items-center justify-between px-3 py-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {sectionNames[section]}
                    </h3>
                  </div>

                  {/* Section Items */}
                  <div className="space-y-1">
                    {items.map((item) => {
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`admin-sidebar-nav ${isActive ? 'active' : ''}`}
                          onClick={() => (isMobile || isTablet) && setSidebarOpen(false)}
                        >
                          <item.icon size={20} className="mr-3 flex-shrink-0" />
                          <span className="truncate">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </nav>

          {/* Founder Badge */}
          <div className="absolute bottom-16 left-0 right-0 px-4">
            <div className="admin-glass-card text-white px-3 py-2 rounded-lg text-center text-sm font-medium">
              <Crown size={16} className="inline mr-2" />
              Founder Access
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Navigation Bar */}
          <header className="admin-header admin-header-gradient">
            <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
              {/* Left side - Menu button and search */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden admin-btn-ghost"
                >
                  <Menu size={20} />
                </button>

                {/* Search - Hidden on mobile, shown on tablet+ */}
                <div className="hidden sm:block admin-search">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search founder panel..."
                    className="admin-search-input w-64 lg:w-80"
                  />
                </div>
              </div>

              {/* Right side - Actions and user menu */}
              <div className="flex items-center space-x-3">
                {/* Mobile search button */}
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className="sm:hidden admin-btn-ghost"
                >
                  <Search size={20} />
                </button>

                {/* Notifications */}
                <button className="admin-btn-ghost relative">
                  <Bell size={20} />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                </button>

                {/* Settings */}
                <button className="admin-btn-ghost">
                  <Settings size={20} />
                </button>

                {/* User menu */}
                <div className="relative user-menu">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="admin-btn-ghost flex items-center space-x-2"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center shadow-md">
                      <Crown size={16} className="text-white" />
                    </div>
                    <ChevronDown size={16} className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {/* User dropdown menu */}
                  {showUserMenu && (
                    <div className="admin-dropdown">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {userProfile?.displayName || currentUser?.displayName || 'Founder'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {currentUser?.email || 'founder@mahstry.com'}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="admin-dropdown-item"
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile search bar */}
            {showSearch && (
              <div className="sm:hidden px-4 pb-4">
                <div className="admin-search">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search founder panel..."
                    className="admin-search-input"
                    autoFocus
                  />
                </div>
              </div>
            )}
          </header>

          {/* Main content area */}
          <main className="flex-1 overflow-auto admin-responsive-padding">
            <div className="container-mobile">
              {children}
            </div>
          </main>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default FounderLayout;

